import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { emailService } from '../services/emailService';
import { Card, CardContent, CardHeader } from '../components/common/Card';
import CommonPageHeader from '../components/common/CommonPageHeader';
import toast from 'react-hot-toast';
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  LanguageIcon
} from '@heroicons/react/24/outline';
import { 
  notifyProjectCreated, 
  notifyTaskCreated, 
  NotificationRecipient 
} from '../services/notificationService';

const EmailTest: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [testEmail, setTestEmail] = useState('nroland89@gmail.com');
  const [testLanguage, setTestLanguage] = useState<'hu' | 'en'>('hu');

  const handleTestEmail = async () => {
    setIsTestingEmail(true);
    setTestResult(null);
    
    try {
      console.log('Testing email sending to:', testEmail);
      
      // Call the Firebase function
      const result = await emailService.sendTestEmail(testEmail);
      
      console.log('Email test result:', result);
      setTestResult(result);
      toast.success('Email sikeresen elküldve!');
      
    } catch (error: any) {
      console.error('Email test failed:', error);
      setTestResult({ error: error.message });
      toast.error(`Email küldés sikertelen: ${error.message}`);
    } finally {
      setIsTestingEmail(false);
    }
  };

  const handleTestNotification = async () => {
    setIsTestingEmail(true);
    setTestResult(null);
    
    try {
      console.log('Testing notification email to:', testEmail);
      
      // Call the notification function
      const result = await emailService.sendNotification({
        to: testEmail,
        subject: 'YouCRM Teszt Értesítés',
        message: 'Ez egy teszt értesítés a YouCRM rendszerből. Ha megkapja ezt az üzenetet, az értesítési rendszer megfelelően működik!',
        organizationName: 'YouCRM Test Organization',
        actionUrl: window.location.origin,
        actionText: 'Tovább a rendszerbe',
        fromName: 'YouCRM Test'
      });
      
      console.log('Notification test result:', result);
      setTestResult(result);
      toast.success('Értesítés sikeresen elküldve!');
      
    } catch (error: any) {
      console.error('Notification test failed:', error);
      setTestResult({ error: error.message });
      toast.error(`Értesítés küldés sikertelen: ${error.message}`);
    } finally {
      setIsTestingEmail(false);
    }
  };

  const handleTestUserInvitation = async () => {
    setIsTestingEmail(true);
    setTestResult(null);
    
    try {
      console.log('Testing user invitation email to:', testEmail);
      
      // Call the user invitation function
      const result = await emailService.sendUserInvitation({
        userEmail: testEmail,
        userName: 'Teszt Felhasználó',
        organizationName: 'YouCRM Test Organization',
        userRole: 'admin',
        organizationId: 'test-org-id'
      });
      
      console.log('User invitation test result:', result);
      setTestResult(result);
      toast.success('Felhasználói meghívó sikeresen elküldve!');
      
    } catch (error: any) {
      console.error('User invitation test failed:', error);
      setTestResult({ error: error.message });
      toast.error(`Meghívó küldés sikertelen: ${error.message}`);
    } finally {
      setIsTestingEmail(false);
    }
  };

  const handleTestProjectNotification = async () => {
    setIsTestingEmail(true);
    setTestResult(null);
    
    try {
      console.log('Testing project notification email to:', testEmail, 'in language:', testLanguage);
      
      // Create mock project data
      const mockProject = {
        id: 'test-project-id',
        name: 'YouCRM Teszt Projekt',
        key: 'TEST',
        description: 'Ez egy teszt projekt a YouCRM rendszerben.',
        status: 'active' as const,
        priority: 'high' as const,
        startDate: new Date(),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days later
        customerId: 'test-customer-id',
        customerName: 'Teszt Ügyfél',
        assignedUsers: [user?.uid || 'test-user-id'],
        assignedUsersNames: [user?.displayName || 'Test User'],
        visibility: 'team' as const,
        taskIds: [],
        progress: 0,
        budget: 100000,
        tags: ['teszt', 'demo'],
        documents: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || 'test-user-id',
        createdByName: user?.displayName || 'Test User',
        organizationId: user?.organizationId || 'test-org-id',
        isActive: true
      };

      // Create recipient
      const recipients: NotificationRecipient[] = [{
        userId: user?.uid || 'test-user-id',
        email: testEmail,
        name: 'Teszt Felhasználó',
        language: testLanguage,
        role: 'admin'
      }];

      // Send notification
      await notifyProjectCreated(
        mockProject,
        recipients,
        'YouCRM Test Organization',
        {
          userId: user?.uid || 'test-user-id',
          name: user?.displayName || 'Test User',
          email: user?.email || 'test@example.com'
        }
      );
      
      setTestResult({ success: true, messageId: 'project-notification-sent' });
      toast.success('Projekt értesítés sikeresen elküldve!');
      
    } catch (error: any) {
      console.error('Project notification test failed:', error);
      setTestResult({ error: error.message });
      toast.error(`Projekt értesítés sikertelen: ${error.message}`);
    } finally {
      setIsTestingEmail(false);
    }
  };

  const handleTestTaskNotification = async () => {
    setIsTestingEmail(true);
    setTestResult(null);
    
    try {
      console.log('Testing task notification email to:', testEmail, 'in language:', testLanguage);
      
      // Create mock task data
      const mockTask = {
        id: 'test-task-id',
        taskNumber: 'TASK-001',
        title: 'YouCRM Teszt Feladat',
        description: 'Ez egy teszt feladat a YouCRM rendszerben.',
        priority: 'medium' as const,
        status: 'todo' as const,
        assignedTo: user?.uid || 'test-user-id',
        assignedToName: user?.displayName || 'Test User',
        createdBy: user?.uid || 'test-user-id',
        createdByName: user?.displayName || 'Test User',
        dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days later
        projectId: 'test-project-id',
        customerId: 'test-customer-id',
        customerName: 'Teszt Ügyfél',
        estimatedHours: 4,
        tags: ['teszt', 'demo'],
        documents: [],
        organizationId: user?.organizationId || 'test-org-id',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Create recipient
      const recipients: NotificationRecipient[] = [{
        userId: user?.uid || 'test-user-id',
        email: testEmail,
        name: 'Teszt Felhasználó',
        language: testLanguage,
        role: 'admin'
      }];

      // Send notification
      await notifyTaskCreated(
        mockTask,
        'YouCRM Teszt Projekt',
        recipients,
        'YouCRM Test Organization',
        {
          userId: user?.uid || 'test-user-id',
          name: user?.displayName || 'Test User',
          email: user?.email || 'test@example.com'
        }
      );
      
      setTestResult({ success: true, messageId: 'task-notification-sent' });
      toast.success('Feladat értesítés sikeresen elküldve!');
      
    } catch (error: any) {
      console.error('Task notification test failed:', error);
      setTestResult({ error: error.message });
      toast.error(`Feladat értesítés sikertelen: ${error.message}`);
    } finally {
      setIsTestingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <CommonPageHeader
        title="Email Teszt"
        subtitle="Firebase Functions email küldés tesztelése"
        icon={EnvelopeIcon}
      />

      {/* Test Configuration */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-medium">Email Teszt Konfigurálása</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teszt Email Cím
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="nroland89@gmail.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Értesítés Nyelve
                </label>
                <select
                  value={testLanguage}
                  onChange={(e) => setTestLanguage(e.target.value as 'hu' | 'en')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="hu">🇭🇺 Magyar</option>
                  <option value="en">🇬🇧 English</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Test Email Button */}
                <button
                  onClick={handleTestEmail}
                  disabled={isTestingEmail}
                  className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTestingEmail ? (
                    <ClockIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  )}
                  Teszt Email
                </button>

                {/* Test Notification Button */}
                <button
                  onClick={handleTestNotification}
                  disabled={isTestingEmail}
                  className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTestingEmail ? (
                    <ClockIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                  )}
                  Értesítés Email
                </button>

                {/* Test User Invitation Button */}
                <button
                  onClick={handleTestUserInvitation}
                  disabled={isTestingEmail}
                  className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTestingEmail ? (
                    <ClockIcon className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  )}
                  Meghívó Email
                </button>
              </div>
              
              {/* Multilingual Notification Tests */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                  <LanguageIcon className="h-4 w-4 mr-2" />
                  Többnyelvű Értesítések ({testLanguage === 'hu' ? 'Magyar' : 'English'})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Test Project Notification Button */}
                  <button
                    onClick={handleTestProjectNotification}
                    disabled={isTestingEmail}
                    className="inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isTestingEmail ? (
                      <ClockIcon className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    )}
                    Projekt Értesítés
                  </button>

                  {/* Test Task Notification Button */}
                  <button
                    onClick={handleTestTaskNotification}
                    disabled={isTestingEmail}
                    className="inline-flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isTestingEmail ? (
                      <ClockIcon className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    )}
                    Feladat Értesítés
                  </button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Results */}
      {testResult && (
        <Card>
          <CardHeader>
            <div className="flex items-center">
              {testResult.error ? (
                <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
              ) : (
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              )}
              <h3 className="text-lg font-medium">
                {testResult.error ? 'Teszt Sikertelen' : 'Teszt Sikeres'}
              </h3>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {testResult.error ? (
                <div className="text-red-600 bg-red-50 p-3 rounded-md">
                  <strong>Hiba:</strong> {testResult.error}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-green-600 bg-green-50 p-3 rounded-md">
                    <strong>Sikeres!</strong> Email sikeresen elküldve.
                  </div>
                  
                  {testResult.messageId && (
                    <div className="text-sm text-gray-600">
                      <strong>Message ID:</strong> {testResult.messageId}
                    </div>
                  )}
                  
                  {testResult.templateId && (
                    <div className="text-sm text-gray-600">
                      <strong>Template ID:</strong> {testResult.templateId}
                    </div>
                  )}
                  
                  <div className="text-sm text-gray-600">
                    <strong>Címzett:</strong> {testEmail}
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <strong>Időpont:</strong> {new Date().toLocaleString('hu-HU')}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Information */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-medium">Rendszer Információ</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm text-gray-600">
            <div><strong>Felhasználó:</strong> {user?.email}</div>
            <div><strong>Szervezet:</strong> {user?.organizationId || 'N/A'}</div>
            <div><strong>Szerepkör:</strong> {user?.role || 'N/A'}</div>
            <div><strong>Firebase Project:</strong> youcrm-40142</div>
            <div><strong>Functions Region:</strong> us-central1</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailTest; 